import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { AccessoryBoDtoNBK, AccessoryService, ImageService } from 'src/app/api/nbk';
import { getDeviceModelList, KEY_LANGUAGE_STORAGE, SelectableDeviceModel } from 'src/app/core/utils';
import { ImageRecipes } from '../../recipe-detail.component';
import { NgSelectComponent, NgOptionTemplateDirective, NgLabelTemplateDirective } from '@ng-select/ng-select';
import { UploadImageComponent } from '../../../upload-image/upload-image.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-create-accessory-modal',
    templateUrl: './create-accessory-modal.component.html',
    styleUrls: ['./create-accessory-modal.component.scss'],
    imports: [ReactiveFormsModule, NgSelectComponent, NgOptionTemplateDirective, 
      NgLabelTemplateDirective, UploadImageComponent, TranslatePipe
    ]
})
export class CreateAccessoryModalComponent implements OnInit {
  @Input() isEdit: boolean = false;
  @Input() isCreate: boolean = false;
  @Input() accessory: AccessoryBoDtoNBK = {};

  @Output() accessoryCreated = new EventEmitter();
  @Output() accessoryUpdated = new EventEmitter();

  accessoryForm: FormGroup;
  fileImageForm: FormControl = this.fb.control('');
  imageUrl: SafeUrl;
  deviceModelList: SelectableDeviceModel[];


  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private accessoryService: AccessoryService,
    private imageService: ImageService
  ) {
    this.deviceModelList = getDeviceModelList();
  }

  ngOnInit(): void {
    this.accessoryForm = this.fb.group({
      name: ['', [Validators.required]],
      description: [''],
      deviceModels: [[], [Validators.required]],
      images: this.fb.array([], [Validators.required])
    });

    if (this.isEdit) {
      this.accessoryForm.controls['deviceModels'].disable();
      this.accessoryForm.patchValue({
        name: this.accessory.name,
        description: this.accessory.description,
        deviceModels: this.accessory.deviceModels!.map((dm: any) => dm.deviceModel) 
      });

      this.accessory.images?.forEach((i) => {
        (this.accessoryForm.controls['images'] as FormArray).push(
          this.fb.group({
            id: [i.id],
            url: [i.url],
            alt: [i.alt],
            s3KeyName: [i.s3KeyName],
            filename: [i.filename]
          })
        );
      });

      this.imageUrl =
        this.accessoryForm.controls['images'].value &&
        this.accessoryForm.controls['images'].value.length > 0
          ? this.accessoryForm.controls['images'].value[0].url
          : null;
    }

  }

  create() {
    if (this.accessoryForm.valid) {
      const newAccesory = { 
        name: this.accessoryForm.controls['name'].value,
        description: this.accessoryForm.controls['description'].value,
        deviceModels: this.accessoryForm.controls['deviceModels'].value.map((m: string) => {
          return {
            id: null,
            deviceModel: m
          }
        })
      };

      this.accessoryService
        .insertAccessory(
          localStorage.getItem(KEY_LANGUAGE_STORAGE)!,
          newAccesory
        )
        .pipe(
          take(1),
          tap(async (acc: any) => {})
        )
        .subscribe(async (acc: any) => {
          const img = this.fileImageForm.value;
          await this.imageService
            .insertImage(img, acc.name, acc.id, undefined, undefined)
            .toPromise();
          this.accessoryCreated.emit(this.accessoryForm.value);
          this.activeModal.close();
        });
    }
  }

  update() {
    if (this.accessoryForm.valid) {
      const accessory = {
        id: this.accessory.id,
        name: this.accessoryForm.controls['name'].value,
        description: this.accessoryForm.controls['description'].value,
        deviceModels: this.accessory.deviceModels,
        images: this.accessoryForm.controls['images'].value,
        creationLanguage: this.accessory.creationLanguage
      };
      const newImage = this.fileImageForm.value;

      const img: ImageRecipes = {
        file: this.fileImageForm.value,
        alt: accessory.name,
        accessory_id: this.accessory.id?.toString()
      };

      this.accessoryService
        .updateAccessory(localStorage.getItem(KEY_LANGUAGE_STORAGE)!, accessory)
        .pipe(
          take(1),
          switchMap(() =>
            accessory.images.length > 0 &&
            accessory.images[0].id === '' &&
            this.accessory.images!.length > 0 &&
            this.accessory.images![0].id
              ? this.imageService.deleteImage(this.accessory.images![0].id!)
              : of({})
          ),
          switchMap(() =>
            img.file
              ? this.imageService.insertImage(
                  img.file,
                  img.alt,
                  img.accessory_id,
                  img.recipe_id,
                  img.step_id
                )
              : of({})
          )
        )
        .subscribe(() => {
          this.accessoryUpdated.emit(this.accessoryForm.value);
          this.activeModal.close();
        });
    }
  }

  imageChanged(image: File | Blob | null | undefined) {
    (this.accessoryForm.controls['images'] as FormArray).clear();
    if ( image ) {
      (this.accessoryForm.controls['images'] as FormArray).push(
        this.fb.group({
          alt: this.accessoryForm.controls['name'],
          id: [''],
          url: ['']
        })
      );
    }
    this.fileImageForm.setValue(image);
  }

}
